"use client";

import { useState, useEffect } from "react";
import { useInstallPrompt } from "@/hooks/useInstallPrompt";
import { Download, Share, X, Plus } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

export default function InstallPrompt() {
    const { deferredPrompt, isIOS, isMobile, isStandalone, promptInstall, isIOSChrome, isIOSSafari } = useInstallPrompt();
    const [showIOSHint, setShowIOSHint] = useState(false);
    // Initialize dismissed state from localStorage during render
    // Initialize as false for hydration match, verify in useEffect
    const [dismissed, setDismissed] = useState(false);

    useEffect(() => {
        if (typeof window !== "undefined" && localStorage.getItem("installPromptDismissed")) {
            setDismissed(true);
        }
    }, []);

    // Debugging
    useEffect(() => {
        console.log("InstallPrompt State:", { isStandalone, dismissed, deferredPrompt: !!deferredPrompt, isMobile, isIOS });
    }, [isStandalone, dismissed, deferredPrompt, isMobile, isIOS]);



    // If already installed or dismissed, strictly return null
    if (isStandalone || dismissed) return null;

    // Show if we have a prompt (Android/Desktop) OR if we are on mobile (fallback instructions)
    // For debugging: Show in dev mode even if not mobile/no-prompt
    const showDebug = process.env.NODE_ENV === "development";

    // Logic: Show if we have a native prompt, OR if on mobile (manual instructions), OR if testing in dev
    const shouldShow = deferredPrompt || isMobile || showDebug;

    if (!shouldShow) return null;

    const handleInstallClick = () => {
        // Prioritize native prompt
        if (deferredPrompt) {
            promptInstall();
        } else {
            // Fallback to manual instructions
            setShowIOSHint(true);
        }
    };

    return (
        <>


            {/* Restoring strictly the previous content, but applying the "Absolute Inset" fix requested earlier and previously implemented */}
            <div className="px-4 pb-4 absolute bottom-24 left-0 right-0 z-[50]">
                {/* Note: I am placing it at bottom-24 to sit above nav bar roughly, 
                      since it is now in layout. 
                      User asked for "absolute". 
                  */}
                <div className="relative">
                    <button
                        onClick={handleInstallClick}
                        className="w-full glass-card flex items-center justify-between p-3 group active:scale-[0.98] transition-all border border-primary/20 bg-primary/5 hover:bg-primary/10"
                    >
                        <div className="flex items-center gap-3">
                            <div className="p-2 rounded-full bg-primary/10 text-primary">
                                <Download size={20} />
                            </div>
                            <div className="text-left">
                                <div className="text-sm font-bold text-white">Add to Home</div>
                                <div className="text-[10px] text-text-muted">For Best Longevity App Experience</div>
                            </div>
                        </div>
                        <div className="text-xs font-bold text-primary bg-primary/10 px-3 py-1.5 rounded-lg mr-6">
                            GET
                        </div>
                    </button>
                    <button
                        onClick={(e) => {
                            e.stopPropagation();
                            setDismissed(true);
                            if (typeof window !== "undefined") {
                                localStorage.setItem("installPromptDismissed", "true");
                            }
                        }}
                        className="absolute right-2 top-1/2 -translate-y-1/2 p-2 text-text-muted hover:text-white"
                    >
                        <X size={16} />
                    </button>
                </div>
            </div>

            {/* iOS Instructions Modal */}
            <AnimatePresence>
                {showIOSHint && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="absolute inset-0 z-[9999] h-full flex items-end sm:items-center justify-center p-4 bg-black/80 backdrop-blur-sm"
                        onClick={() => setShowIOSHint(false)}
                    >
                        <motion.div
                            initial={{ y: 100 }}
                            animate={{ y: 0 }}
                            exit={{ y: 100 }}
                            onClick={(e) => e.stopPropagation()}
                            className="w-full max-w-sm glass-card p-6 space-y-6 relative"
                        >
                            <button
                                onClick={() => setShowIOSHint(false)}
                                className="absolute right-4 top-4 text-text-muted hover:text-white"
                            >
                                <X size={20} />
                            </button>

                            <div className="space-y-4">
                                <h3 className="text-lg font-bold text-center">
                                    Add to Home
                                </h3>

                                <div className="space-y-3">
                                    {isIOS ? (
                                        <>
                                            <div className="flex items-center gap-3 text-sm p-3 bg-white/5 rounded-xl">
                                                <Share size={18} className="text-primary" />
                                                <span>
                                                    Tap <b>Share</b> {isIOSChrome ? "(top right)" : isIOSSafari ? "(bottom bar)" : ""}
                                                </span>
                                            </div>
                                            <div className="flex items-center gap-3 text-sm p-3 bg-white/5 rounded-xl">
                                                <div className="w-[18px] h-[18px] border-2 border-primary rounded-[4px] flex items-center justify-center">
                                                    <Plus size={12} className="text-primary" />
                                                </div>
                                                <span>Select <b>Add to Home Screen</b></span>
                                            </div>
                                        </>
                                    ) : (
                                        <>
                                            <div className="flex items-center gap-3 text-sm p-3 bg-white/5 rounded-xl">
                                                <div className="font-bold text-primary">1</div>
                                                <span>Tap browser menu (three dots)</span>
                                            </div>
                                            <div className="flex items-center gap-3 text-sm p-3 bg-white/5 rounded-xl">
                                                <div className="font-bold text-primary">2</div>
                                                <span>Select <b>Add to Home</b></span>
                                            </div>
                                        </>
                                    )}
                                </div>
                            </div>

                            {isIOS && (
                                <div className="pt-2 flex justify-center text-primary/50">
                                    <div className="animate-bounce">
                                        <ArrowDown />
                                    </div>
                                </div>
                            )}
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </>
    );
}

function ArrowDown() {
    return (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="12" y1="5" x2="12" y2="19"></line>
            <polyline points="19 12 12 19 5 12"></polyline>
        </svg>
    )
}
